import { useState, useContext, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { AppContext } from "../../../pages/root/Root";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Button,
  IconButton,
  Modal,
} from "@mui/material";
import {
  Refresh,
  Download,
  AccountCircleOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";

import { AppAlert } from "../../common/appAlert/AppAlert";
import { DataTable } from "../../common/dataTable/DataTable";
import { MappingKeysTimer } from "../../common/mappingKeysTimer/MappingKeysTimer";
import { DataAssignmentFilters } from "../dataAssignmentFilters/DataAssignmentFilters";
import { BuyerStatusColored } from "../buyerStatusColored/BuyerStatusColored";
import { ViewMappingDetails } from "../../common/viewMappingDetails/ViewMappingDetails";
import { ViewMappingDetailsMetadata } from "../viewMappingDetailsMetadata/ViewMappingDetailsMetadata";

import useIsMounted from "../../../hooks/common/useIsMounted";
import useNotificationHub from "../../../hooks/common/useNotificationHub";
import useBuyersData from "../../../hooks/eRetail/useBuyersData";
import useViewMappingDetails from "../../../hooks/eRetail/useViewMappingDetails";

import {
  downloadFile,
  initLocalization,
  createERetailMappingDetailsData,
} from "../../../utils/helpers";
import { dataAssignmentStrings } from "./locale";
import {
  MAPPING_KEYS_DATE_FORMAT,
  DEFAULT_TABLE_PAGINATION,
  DEFAULT_E_RETAIL_DATA_ASSIGNMENT_FILTERS,
  DEFAULT_E_RETAIL_DATA_ASSIGNMENT_SORTING,
  DEFAULT_E_RETAIL_DATA_ASSIGNMENT_EXCLUSIONS,
} from "../../../constants";

export const DataAssignment = ({
  isLoading,
  shouldRefresh,
  onBuyersSelection,
}) => {
  initLocalization(dataAssignmentStrings);

  const isMounted = useIsMounted();

  const { user, language } = useContext(AppContext);

  const lastSelected = useRef(null);
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [pagination, setPagination] = useState(DEFAULT_TABLE_PAGINATION);
  const [filters, setFilters] = useState(
    DEFAULT_E_RETAIL_DATA_ASSIGNMENT_FILTERS,
  );
  const [exclusions, setExclusions] = useState(
    DEFAULT_E_RETAIL_DATA_ASSIGNMENT_EXCLUSIONS,
  );
  const [sorting, setSorting] = useState(
    DEFAULT_E_RETAIL_DATA_ASSIGNMENT_SORTING,
  );
  const [scheduleTimer, setScheduleTimer] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const {
    isLoading: buyersLoading,
    isExporting,
    buyersData,
    getBuyersData,
    setBuyersData,
    exportBuyersData,
    lockBuyerLines,
  } = useBuyersData();

  const {
    mappingDetails,
    getMappingDetails,
    isLoading: isDetailsLoading,
  } = useViewMappingDetails();

  const { message: updatedBuyerRows } = useNotificationHub();

  const buyersRows = useMemo(() => {
    if (updatedBuyerRows?.lineIds?.length) {
      const shouldLock = updatedBuyerRows.status === "Lock";
      const isCurrentUser = updatedBuyerRows.userId === user.id;

      return buyersData?.data?.map((row) => {
        // Prevent unlock visual flash when multiple selecting
        if (!shouldLock && selectedRows.has(row.id)) {
          return row;
        }

        if (updatedBuyerRows.lineIds.includes(row.id)) {
          row.isLocked = shouldLock;
          row.isLockedByCurrentUser = isCurrentUser && shouldLock;
        }

        return row;
      });
    }

    return buyersData?.data;
  }, [buyersData, updatedBuyerRows]);

  const columns = [
    {
      field: "advertiserName",
      headerName: dataAssignmentStrings.adName,
      minWidth: 150,
    },
    {
      field: "advertiserPaid",
      headerName: dataAssignmentStrings.adPaid,
      minWidth: 200,
    },
    {
      field: "numberOfOccurrences",
      headerName: dataAssignmentStrings.number,
      minWidth: 45,
    },
    {
      field: "status",
      headerName: dataAssignmentStrings.status,
      minWidth: 62,
      /* NOSONAR */ format: (value, row) => (
        <BuyerStatusColored value={value} row={row} />
      ),
    },
    {
      field: "firstOccurrence",
      headerName: dataAssignmentStrings.date,
      minWidth: 45,
      format: (value) => dayjs(value).format(MAPPING_KEYS_DATE_FORMAT),
    },
  ];

  const resetTable = async (showLoader = true) => {
    setScheduleTimer(false);

    await lockBuyerLines(
      {
        shouldLock: false,
      },
      showLoader,
    );

    setSelectedRows(() => new Set());
    onBuyersSelection([]);
    lastSelected.current = null;
  };

  const updateTable = async (overrides = {}) => {
    await resetTable();

    return getBuyersData({
      ...pagination,
      pageNumber: pagination.pageNumber + 1,
      ...filters,
      ...exclusions,
      ...sorting,
      ...overrides,
    });
  };

  const lockLines = async (selection) => {
    try {
      setScheduleTimer(false);
      // Reset locked lines. Stop execution if unlocking fails
      await lockBuyerLines({
        shouldLock: false,
      }).catch((error) => {
        throw new Error(error[0]);
      });

      if (selection.length) {
        await lockBuyerLines({
          shouldLock: true,
          lineIds: selection,
        })
          .then(() => setScheduleTimer(true))
          .catch((error) => {
            setError(error[0]);
            console.log(error[0]);
          });
      }
    } catch (err) {
      setError(err.message);
      console.log(err.message);
    }
  };

  const handleSearch = async () => {
    const pageNumber = 0;
    await updateTable({ pageNumber: pageNumber + 1 });
    setPagination({ ...pagination, pageNumber });
    setSorting(sorting);
  };

  const handleViewCreatives = async () => {
    await getMappingDetails([...selectedRows]);
    handleOpenModal();
  };

  const handleExport = async () => {
    exportBuyersData({
      ...filters,
      ...exclusions,
      ...sorting,
      userLanguage: language,
    })
      .then((data) => {
        downloadFile(
          `eretail-${dataAssignmentStrings.title
            .replaceAll(" ", "-")
            .toLowerCase()}`,
          "xlsx",
          data,
        );
      })
      .catch((error) => {
        setError(error);
      });
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_E_RETAIL_DATA_ASSIGNMENT_FILTERS);
    setExclusions(DEFAULT_E_RETAIL_DATA_ASSIGNMENT_EXCLUSIONS);
    setPagination({ ...pagination, pageNumber: 0 });
    resetTable(false);
    setBuyersData([]);
  };

  const getSelected = (currentIndex, allowMultiple, indices) => {
    const prevArray = [...selectedRows];
    const current = prevArray.find((id) => id === buyersRows[currentIndex].id);
    const currentSet =
      current && prevArray.length === 1 ? new Set([current]) : new Set();
    const next = allowMultiple ? new Set(selectedRows) : currentSet;

    indices.forEach((idx) => {
      const id = buyersRows[idx].id;
      next.has(id) ? next.delete(id) : next.add(id);
    });

    return next;
  };

  const handleSelectRow = async (event, index) => {
    if (
      buyersRows[index].isLocked &&
      !buyersRows[index].isLockedByCurrentUser
    ) {
      return;
    }

    const indices = new Set();
    let allowMultiple = false;

    indices.add(index);

    if (event.shiftKey) {
      const limits = [lastSelected.current ?? 0, index].sort((a, b) => a - b);
      const [start, end] = limits;

      for (let i = start; i <= end; i++) {
        if (buyersRows[i].isLocked && !buyersRows[i].isLockedByCurrentUser) {
          continue;
        }
        indices.add(i);
      }

      indices.delete(lastSelected.current);
      allowMultiple = true;
    } else if (event.metaKey || event.ctrlKey) {
      allowMultiple = true;
    }

    const newSelection = getSelected(index, allowMultiple, indices);

    setSelectedRows(newSelection);
    onBuyersSelection(
      [...buyersRows].filter((entry) => newSelection.has(entry.id)),
    );

    lastSelected.current = index;
  };

  const handleSort = async (columnId, direction) => {
    if (!buyersRows.length) {
      return;
    }

    const sortColumn = columnId.charAt(0).toUpperCase() + columnId.slice(1);

    await updateTable({ pageNumber: 1, sortOption: { sortColumn, direction } });
    setPagination({ ...pagination, pageNumber: 0 });
    setSorting({ ...sorting, sortOption: { sortColumn, direction } });
  };

  const handleChangePage = async (event, newPage) => {
    await updateTable({ pageNumber: newPage + 1 });
    setPagination({ ...pagination, pageNumber: newPage });
  };

  const handleChangeRowsPerPage = async (event) => {
    const value = parseInt(event.target.value, 10);
    await updateTable({ pageNumber: 1, pageSize: value });
    setPagination({ ...pagination, pageNumber: 0, pageSize: value });
  };

  const handleCloseError = () => setError(false);

  useEffect(() => {
    if (lastSelected.current === null) {
      return;
    }

    lockLines([...selectedRows]);
  }, [selectedRows]);

  useEffect(() => {
    if (shouldRefresh) {
      updateTable();
    }
  }, [shouldRefresh]);

  useEffect(() => {
    if (isMounted && filters.status) {
      handleSearch();
    }
  }, [filters.status]);

  return (
    <Paper sx={{ padding: "0.5rem", height: "100%", position: "relative" }}>
      <AppAlert
        key={error}
        open={Boolean(error)}
        type="error"
        onClose={handleCloseError}
      >
        {dataAssignmentStrings.errors[error]}
      </AppAlert>

      <Grid container direction="column" height="100%">
        <Grid xs={12} container justifyContent="space-between" minHeight="32px">
          <Typography>{dataAssignmentStrings.title}</Typography>
          <Grid container>
            <MappingKeysTimer
              scheduleTimer={scheduleTimer}
              setSelectedRowsCB={() => setSelectedRows(() => new Set())}
            />
            <Button
              startIcon={<AccountCircleOutlined />}
              disabled={!selectedRows.size}
              variant="contained"
              color="secondary"
              onClick={handleViewCreatives}
              sx={{ ml: 2 }}
            >
              {dataAssignmentStrings.viewCreativesButton}
            </Button>
            <Button
              startIcon={
                isExporting ? (
                  <CircularProgress size={18} sx={{ opacity: 0.5 }} />
                ) : (
                  <Download />
                )
              }
              variant="outlined"
              disabled={!buyersRows?.length || isExporting}
              onClick={handleExport}
              sx={{ ml: 2 }}
            >
              {dataAssignmentStrings.exportButton}
            </Button>
            <Button
              startIcon={<Refresh sx={{ transform: "scaleX(-1)" }} />}
              variant="text"
              onClick={handleResetFilters}
              sx={{ ml: 2 }}
            >
              {dataAssignmentStrings.resetFiltersButton}
            </Button>
          </Grid>
        </Grid>

        <Grid xs={12} paddingY={3}>
          <DataAssignmentFilters
            filters={filters}
            setFilters={setFilters}
            exclusions={exclusions}
            setExclusions={setExclusions}
            onSearch={handleSearch}
          />
        </Grid>

        <Grid xs={12} display="flex" flexDirection="column" flexGrow="1">
          <DataTable
            columns={columns}
            rows={buyersRows || []}
            selectedRows={[...selectedRows]}
            page={pagination.pageNumber}
            rowsPerPage={pagination.pageSize}
            totalRecords={buyersData.totalRecords}
            sortBy={
              sorting.sortOption.sortColumn.charAt(0).toLowerCase() +
              sorting.sortOption.sortColumn.slice(1)
            }
            sortDirection={sorting.sortOption.direction}
            sortableColumns={columns.map((column) => column.field)}
            isLockedKeys={["isLocked"]}
            isLockedByUserKey="isLockedByCurrentUser"
            onSelectRow={handleSelectRow}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            onSort={handleSort}
            showNoDataMessage={false}
          />
        </Grid>
      </Grid>

      <Modal
        open={openModal && !isDetailsLoading}
        onClose={handleCloseModal}
        aria-describedby="view-creatives-modal"
        sx={{
          "&": { zIndex: (theme) => theme.zIndex.drawer - 1, top: 50 },
          "& .MuiModal-backdrop": { top: 50 },
        }}
      >
        <Grid
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "calc(100vh - 50px)",
            bgcolor: "#fff",
          }}
          className="view-creatives-modal"
          id="view-creatives-modal"
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon color="primary" sx={{ width: 20, height: 20 }} />
          </IconButton>
          <ViewMappingDetails
            mappingDetailsData={mappingDetails}
            metadataComponent={(props) => (
              <ViewMappingDetailsMetadata {...props} />
            )}
            dataTransformer={createERetailMappingDetailsData}
          />
        </Grid>
      </Modal>

      <Backdrop
        open={isLoading || buyersLoading}
        sx={{
          position: "absolute",
          color: "#fff",
          borderRadius: 1,
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

DataAssignment.propTypes = {
  isLoading: PropTypes.bool,
  shouldRefresh: PropTypes.number,
  onBuyersSelection: PropTypes.func.isRequired,
};
